import { Component, EventEmitter, HostListener, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { Catalog, CatalogRequest } from '../../models/catalog';
import { CatalogService } from '../../../services/catalog.service';

/**
 * Creates a catalog, or edits one's code, name, description and source.
 *
 * One dialog for both because the backend takes the same body on POST and PUT, and both the list
 * ("New catalog", "Edit") and the detail page ("Edit") open it. It saves on its own and hands
 * the saved catalog back, so neither caller repeats the request or its error handling.
 *
 * Declarative like ConfirmDialogComponent, and styled the same way.
 */
@Component({
  selector: 'app-catalog-form-dialog',
  templateUrl: './catalog-form-dialog.component.html',
  styleUrls: ['./catalog-form-dialog.component.scss']
})
export class CatalogFormDialogComponent implements OnChanges {

  /** The backend's own rule (CatalogCreateDTO), checked here so a typo is caught before a round trip. */
  static readonly CODE_PATTERN = /^[A-Za-z0-9_.-]+$/;

  @Input() isOpen = false;
  /** The catalog to edit, or null to create one. */
  @Input() catalog: Catalog | null = null;

  @Output() saved = new EventEmitter<Catalog>();
  @Output() cancel = new EventEmitter<void>();

  code = '';
  name = '';
  description = '';
  sourceUrl = '';

  saving = false;
  error: string | null = null;

  constructor(private catalogService: CatalogService) {}

  ngOnChanges(changes: SimpleChanges): void {
    // Refill on every open, so a cancelled edit does not leave its half-typed values for the next.
    if (changes['isOpen'] && this.isOpen) {
      this.code = this.catalog?.code ?? '';
      this.name = this.catalog?.name ?? '';
      this.description = this.catalog?.description ?? '';
      this.sourceUrl = this.catalog?.sourceUrl ?? '';
      this.error = null;
      this.saving = false;
    }
  }

  get isEdit(): boolean {
    return !!this.catalog;
  }

  get codeInvalid(): boolean {
    return this.code.trim() !== '' && !CatalogFormDialogComponent.CODE_PATTERN.test(this.code.trim());
  }

  get canSave(): boolean {
    return !this.saving && this.code.trim() !== '' && this.name.trim() !== '' && !this.codeInvalid;
  }

  save(): void {
    if (!this.canSave) {
      return;
    }
    const request: CatalogRequest = {
      code: this.code.trim(),
      name: this.name.trim(),
      description: this.description.trim() || null,
      sourceUrl: this.sourceUrl.trim() || null
    };

    this.saving = true;
    this.error = null;
    const call = this.catalog
      ? this.catalogService.updateCatalog(this.catalog.id, request)
      : this.catalogService.createCatalog(request);

    call.subscribe({
      next: (catalog) => {
        this.saving = false;
        this.saved.emit(catalog);
      },
      error: (err) => {
        this.saving = false;
        this.error = err?.error?.exceptionMessage || err?.error?.message || 'The catalog could not be saved.';
      }
    });
  }

  close(): void {
    if (!this.saving) {
      this.cancel.emit();
    }
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    if (this.isOpen) {
      this.close();
    }
  }
}
